import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import imgFears from "@/assets/archetype-fears.webp";
import imgAnger from "@/assets/archetype-anger.webp";
import imgConfidence from "@/assets/archetype-confidence.webp";
import imgJealousy from "@/assets/archetype-jealousy.webp";
import imgAdaptation from "@/assets/archetype-adaptation.webp";
import imgGrief from "@/assets/archetype-grief.webp";
import imgAutonomy from "@/assets/archetype-autonomy.webp";
import imgHelp from "@/assets/archetype-help.webp";
import ArchetypeQuiz from "./ArchetypeQuiz";

const archetypes = [
  {
    id: "fears",
    image: imgFears,
    title: "Свет в темноте",
    subtitle: "Помогаем справиться со страхами и тревогой",
    modal: {
      description: "Эти истории помогают ребёнку безопасно прожить страх.",
      details:
        "Мы не убеждаем, что «бояться нечего». Мы создаём героя, который постепенно чувствует поддержку и находит свет внутри себя.",
      points: [
        "почувствовал, что страх — это нормально",
        "увидел символ поддержки",
        "завершил историю ощущением безопасности",
      ],
    },
  },
  {
    id: "anger",
    image: imgAnger,
    title: "Сердитый дракон",
    subtitle: "Учимся понимать и проживать злость",
    modal: {
      description: "Эти истории помогают ребёнку принять свою злость и найти ей безопасный выход.",
      details:
        "Мы не запрещаем злиться. Мы показываем героя, который учится понимать свои чувства и находит способ выразить их без разрушения.",
      points: [
        "понял, что злиться — это нормально",
        "нашёл безопасный способ выразить злость",
        "почувствовал облегчение и принятие",
      ],
    },
  },
  {
    id: "confidence",
    image: imgConfidence,
    title: "Маленький герой",
    subtitle: "Поддерживаем уверенность и смелость",
    modal: {
      description: "Эти истории укрепляют внутреннюю опору ребёнка.",
      details:
        "Мы создаём ситуации, где герой шаг за шагом обнаруживает свои сильные стороны и учится верить в себя.",
      points: [
        "почувствовал свою силу",
        "справился с маленьким вызовом",
        "завершил историю с уверенностью в себе",
      ],
    },
  },
  {
    id: "jealousy",
    image: imgJealousy,
    title: "Большое сердце",
    subtitle: "Помогаем принять ревность и изменения в семье",
    modal: {
      description: "Эти истории помогают ребёнку принять изменения в семье.",
      details:
        "Мы не говорим «не ревнуй». Мы создаём пространство, где герой учится находить своё место в изменившемся мире.",
      points: [
        "почувствовал, что его любят так же сильно",
        "нашёл своё особенное место",
        "завершил историю ощущением принятия",
      ],
    },
  },
  {
    id: "adaptation",
    image: imgAdaptation,
    title: "Новый мир",
    subtitle: "Мягкая адаптация к детскому саду или школе",
    modal: {
      description: "Эти истории помогают ребёнку привыкнуть к новому окружению.",
      details:
        "Мы не торопим и не обесцениваем тревогу. Герой постепенно находит что-то тёплое и знакомое в новом пространстве.",
      points: [
        "почувствовал, что новое — не значит страшное",
        "нашёл друга или опору в новом месте",
        "завершил историю ощущением спокойствия",
      ],
    },
  },
  {
    id: "grief",
    image: imgGrief,
    title: "Потерянная звезда",
    subtitle: "Бережно проживаем грусть и утрату",
    modal: {
      description: "Эти истории помогают ребёнку прожить грусть и потерю.",
      details:
        "Мы не говорим «не грусти». Мы создаём метафору, через которую ребёнок может безопасно прикоснуться к чувствам утраты и найти внутренний свет.",
      points: [
        "почувствовал, что грустить — можно",
        "нашёл тёплое воспоминание или символ",
        "завершил историю ощущением тепла и памяти",
      ],
    },
  },
  {
    id: "autonomy",
    image: imgAutonomy,
    title: "Внутренний компас",
    subtitle: "Учимся делать выбор и чувствовать самостоятельность",
    modal: {
      description: "Эти истории помогают ребёнку почувствовать радость от самостоятельных решений.",
      details:
        "Мы не решаем за ребёнка. Герой оказывается перед выбором и учится слушать себя — свой внутренний компас.",
      points: [
        "почувствовал, что его мнение важно",
        "сделал выбор и увидел результат",
        "завершил историю с чувством «я могу»",
      ],
    },
  },
];

interface Props {
  onSelect: (goal: string) => void;
  onBack: () => void;
}

const GoalSelect = ({ onSelect, onBack }: Props) => {
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const activeArchetype = archetypes.find((a) => a.id === openModal);

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-12 relative">
      <button
        onClick={onBack}
        className="fixed top-4 left-4 z-50 w-9 h-9 rounded-full flex items-center justify-center text-foreground bg-card/70 backdrop-blur-sm hover:bg-card transition-colors"
        aria-label="Назад"
      >
        <ChevronLeft size={20} />
      </button>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-3xl space-y-10"
      >
        {/* Hero */}
        <div className="space-y-3">
            <h2 className="text-2xl md:text-3xl font-serif text-foreground text-center w-full">
              Что сейчас важно ребёнку?
            </h2>
          <p className="text-base text-muted-foreground font-sans max-w-md mx-auto text-center">
            Мы создаём историю по мягкой структуре, подходящей к каждой ситуации.
          </p>
          <div className="w-16 h-px bg-border mx-auto mt-4" />
        </div>

        {/* Archetypes grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {archetypes.map((arch, i) => (
            <motion.div
              key={arch.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              className="rounded-3xl bg-card border border-border overflow-hidden hover:shadow-md hover:border-secondary transition-all group relative"
            >
              {/* Color stripe */}
              <div
                className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
                style={{ backgroundColor: `hsl(var(--archetype-${arch.id}))` }}
              />
              <div className="aspect-square overflow-hidden">
                <img
                  src={arch.image}
                  alt={arch.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 space-y-3">
                <div className="space-y-1">
                  <h3 className="font-serif text-lg text-foreground font-semibold">
                    {arch.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-sans">
                    {arch.subtitle}
                  </p>
                </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setOpenModal(arch.id)}
                  className="text-xs font-sans text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  Подробнее
                </button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelect(arch.id)}
                  className="ml-auto px-5 py-2 rounded-2xl bg-primary text-primary-foreground font-sans font-medium text-sm transition-shadow hover:shadow-md"
                >
                  Выбрать
                </motion.button>
              </div>
              </div>
            </motion.div>
          ))}

          {/* Help card */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + archetypes.length * 0.05 }}
            className="rounded-3xl bg-card border border-border overflow-hidden hover:shadow-md hover:border-secondary transition-all group"
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={imgHelp}
                alt="Помочь выбрать"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5 space-y-3">
              <div className="space-y-1">
                <h3 className="font-serif text-lg text-foreground font-semibold">
                  Не знаете, что выбрать?
                </h3>
                <p className="text-sm text-muted-foreground font-sans">
                  Ответьте на 3 коротких вопроса — и мы подскажем подходящую историю
                </p>
              </div>
              <div className="flex gap-2 pt-1">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setQuizOpen(true)}
                  className="ml-auto px-5 py-2 rounded-2xl bg-secondary text-secondary-foreground font-sans font-medium text-sm transition-shadow hover:shadow-md"
                >
                  Помочь выбрать
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Methodology footer */}
        <p className="text-center text-xs text-muted-foreground/60 font-sans pt-4 pb-8">
          Все истории создаются по методу мягкой трансформации: Нормализация →
          Символ → Проживание → Поддержка → Разговор
        </p>
      </motion.div>

      {/* Archetype detail modal */}
      <Dialog open={!!openModal} onOpenChange={() => setOpenModal(null)}>
        <DialogContent
          className="rounded-3xl max-w-md"
          style={activeArchetype ? {
            borderTop: `3px solid hsl(var(--archetype-${activeArchetype.id}))`,
            boxShadow: `0 8px 30px -8px hsl(var(--archetype-${activeArchetype.id}) / 0.15)`,
          } : {}}
        >
          {activeArchetype && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-xl flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: `hsl(var(--archetype-${activeArchetype.id}))` }}
                  />
                  {activeArchetype.title}
                </DialogTitle>
                <DialogDescription className="pt-2 font-sans text-base text-foreground/80 leading-relaxed">
                  {activeArchetype.modal.description}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                  {activeArchetype.modal.details}
                </p>
                <div className="space-y-1">
                  <p className="font-sans text-sm font-medium text-foreground">
                    История строится так, чтобы ребёнок:
                  </p>
                  <ul className="space-y-1.5">
                    {activeArchetype.modal.points.map((point, i) => (
                      <li
                        key={i}
                        className="font-sans text-sm text-muted-foreground pl-4 border-l-2"
                        style={{ borderColor: `hsl(var(--archetype-${activeArchetype.id}) / 0.4)` }}
                      >
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    setOpenModal(null);
                    onSelect(activeArchetype.id);
                  }}
                  className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-sans font-semibold text-base transition-shadow hover:shadow-lg"
                >
                  Выбрать эту тему
                </motion.button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quiz modal */}
      <ArchetypeQuiz
        open={quizOpen}
        onOpenChange={setQuizOpen}
        onSelect={onSelect}
      />
    </div>
  );
};

export default GoalSelect;
